/**
 * Minimum time a stage is highlighted in a build being watched. This is presentation only:
 * persisted states and elapsed work stay immediate. The screen announces success only after
 * the last displayed stage; failures still cancel the presentation immediately.
 * A completed build opened later is never replayed; a failure cancels the presentation queue.
 */
import { useEffect, useState } from "react";

export const MIN_STAGE_VISIBLE_MS = 5_000;

type PresentationPhase = "queued" | "waiting" | "running" | "succeeded" | "failed";
interface StageFact { readonly ordinal: number; readonly state: string }
export interface StagePresentation {
  readonly kind: "waiting" | "watching" | "finished";
  readonly ordinal: number | null;
  /** Monotonic browser time: independent of the server's timestamps and wall-clock adjustments. */
  readonly shownAtMs: number;
}
export const EMPTY_STAGE_PRESENTATION: StagePresentation = { kind: "waiting", ordinal: null, shownAtMs: 0 };

/** Advance at most one stage: a delayed browser timer must not skip stages to catch up. */
export function presentStage(
  previous: StagePresentation,
  stage: readonly StageFact[],
  phase: PresentationPhase,
  atMs: number,
  heardLiveEvent: boolean,
): StagePresentation {
  if (phase === "failed") {
    return previous.kind === "finished" ? previous : { kind: "finished", ordinal: null, shownAtMs: atMs };
  }
  if (previous.kind === "finished" || phase === "waiting" || phase === "queued") return previous;
  const ordered = [...stage].sort((a, b) => a.ordinal - b.ordinal);
  if (previous.kind === "waiting") {
    if (phase === "succeeded" && !heardLiveEvent) return { kind: "finished", ordinal: null, shownAtMs: atMs };
    const first = phase === "succeeded" ? ordered.find((one) => one.state === "succeeded") : ordered.find((one) => one.state === "running");
    return first === undefined ? previous : { kind: "watching", ordinal: first.ordinal, shownAtMs: atMs };
  }
  const focused = ordered.find((one) => one.ordinal === previous.ordinal);
  if (focused?.state !== "succeeded" || atMs - previous.shownAtMs < MIN_STAGE_VISIBLE_MS) return previous;
  const next = ordered.find((one) => one.ordinal > focused.ordinal && (one.state === "running" || one.state === "succeeded"));
  if (next !== undefined) return { kind: "watching", ordinal: next.ordinal, shownAtMs: atMs };
  if (phase === "succeeded" && ordered.every((one) => one.state === "succeeded")) {
    return { kind: "finished", ordinal: null, shownAtMs: atMs };
  }
  return previous;
}

export function useStagePresentation(stage: readonly StageFact[], phase: PresentationPhase, heardLiveEvent: boolean): { readonly ordinal: number | null; readonly pending: boolean } {
  const [shown, setShown] = useState(EMPTY_STAGE_PRESENTATION);
  // The browser timer advances presentation after a committed frame. Never change build state.
  useEffect(() => {
    const atMs = performance.now();
    let delay = 0;
    if (presentStage(shown, stage, phase, atMs, heardLiveEvent) === shown) {
      if (shown.kind !== "watching") return;
      delay = Math.max(0, shown.shownAtMs + MIN_STAGE_VISIBLE_MS - atMs);
      if (presentStage(shown, stage, phase, atMs + delay, heardLiveEvent) === shown) return;
    }
    const timer = setTimeout(() => {
      setShown((previous) => presentStage(previous, stage, phase, performance.now(), heardLiveEvent));
    }, Math.ceil(delay));
    return () => { clearTimeout(timer); };
  }, [shown, stage, phase, heardLiveEvent]);
  return {
    ordinal: phase === "failed" ? null : shown.ordinal,
    // Hold success even before the first browser timer when stream updates were batched.
    pending: phase === "succeeded" && shown.kind !== "finished" &&
      (shown.kind === "watching" || (heardLiveEvent && stage.some((one) => one.state === "succeeded"))),
  };
}
